import React, { useEffect, useRef } from "react";
import { SearchSuggestionsListProps } from "./types";
import PulseLoader from "../Loader/PulseLoader";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";

const SearchSuggestionsList: React.FC<SearchSuggestionsListProps> = ({
  listboxId,
  suggestionId,
  suggestionsClassName = "",
  filteredSuggestions,
  selectedIndex,
  suggestionItemClassName = "",
  onSuggestionSelect,
  noSuggestionsText = "No suggestions found",
  loadingText = "",
  isLoading = false,
  isLoadingMore = false,
  hasMoreSuggestionsToLoad,
  handleApiSearchLoadMore,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const sentinel = loadMoreRef.current;

    if (
      !container ||
      !sentinel ||
      !hasMoreSuggestionsToLoad ||
      !handleApiSearchLoadMore
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !isLoading &&
          !isLoadingMore &&
          hasMoreSuggestionsToLoad
        ) {
          handleApiSearchLoadMore?.();
        }
      },
      {
        root: container,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    filteredSuggestions.length,
    handleApiSearchLoadMore,
    hasMoreSuggestionsToLoad,
    isLoading,
    isLoadingMore,
  ]);

  if (isLoading) {
    return (
      <div
        id={listboxId}
        role="listbox"
        className={`absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto scrollbar-thin-gray border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[8px] shadow-popper-blur-effect ${suggestionsClassName}`}
      >
        <div className="flex items-center justify-center py-4 text-md text-heading-text dark:text-heading-text-dk">
          <PulseLoader size={4} />
          {loadingText && <span>{loadingText}</span>}
        </div>
      </div>
    );
  }

  if (filteredSuggestions.length === 0) {
    return (
      <div
        id={listboxId}
        role="listbox"
        className={`absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto scrollbar-thin-gray border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[8px] shadow-popper-blur-effect ${suggestionsClassName}`}
      >
        <div className="py-4 px-3 text-md text-heading-text dark:text-heading-text-dk text-center">
          {noSuggestionsText}
        </div>
      </div>
    );
  }

  return (
    <div
      id={listboxId}
      role="listbox"
      ref={containerRef}
      className={`absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto ${visibleScrollbar(
        "[&::-webkit-scrollbar]:w-[8px]"
      )} border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[8px] shadow-popper-blur-effect ${suggestionsClassName}`}
    >
      {filteredSuggestions.map((suggestion, index) => {
        const suggestionValue =
          typeof suggestion === "string" ? suggestion : suggestion.value;
        const isSelected = index === selectedIndex;

        return (
          <div
            key={suggestionValue}
            id={suggestionId(index)}
            role="option"
            aria-selected={isSelected}
            className={`px-3 py-2 cursor-pointer transition-colors ${
              isSelected
                ? "bg-verba-blue dark:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk"
                : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk text-heading-text dark:text-heading-text-dk"
            } ${suggestionItemClassName}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onSuggestionSelect(suggestionValue, index);
            }}
          >
            {typeof suggestion === "string" ? (
              <span className="text-md">{suggestion}</span>
            ) : (
              suggestion.content
            )}
          </div>
        );
      })}
      {!isLoading && !isLoadingMore && hasMoreSuggestionsToLoad && (
        <div ref={loadMoreRef} />
      )}
      {isLoadingMore && (
        <div className="flex items-center justify-center py-4 text-md text-heading-text dark:text-heading-text-dk">
          <PulseLoader size={4} />
          {loadingText && <span>{loadingText}</span>}
        </div>
      )}
    </div>
  );
};

export default SearchSuggestionsList;
